import { useCallback, useEffect, useState } from 'react';

/**
 * Hook for Media Hit List horizontal navigation with the following features:
 * - Left/right navigation buttons advance exactly one card per click
 * - Alignment is pixel-perfect across breakpoints where container padding changes
 * - No hardcoded measurements; alignment derives from container's actual padding
 * - Buttons are disabled when at boundaries and when there's no overflow
 * - Buttons are hidden below md breakpoint (handled by CSS)
 * - Scroll-snap behavior for mobile manual scrolling (handled by CSS)
 * - Resilient to dynamic changes in content and viewport size
 */

const getContainerPadding = (container: HTMLDivElement) => {
	const styles = window.getComputedStyle(container);
	return parseInt(styles.paddingLeft, 10) || 0;
};

const getAllCards = (container: HTMLDivElement): HTMLElement[] => {
	const cards = container.querySelectorAll<HTMLElement>(
		'[data-testid^="media-hit-"], [data-testid="media-hit-loading"]',
	);
	if (cards.length > 0) return Array.from(cards);

	return Array.from(container.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
};

const getCardPosition = (card: HTMLElement, container: HTMLDivElement): number => {
	try {
		const containerRect = container.getBoundingClientRect();
		const cardRect = card.getBoundingClientRect();
		const position = cardRect.left - containerRect.left;
		if (position !== 0) return position;
	} catch {
		// Fallback for test environments
	}
	return card.offsetLeft - container.scrollLeft;
};

const findVisibleCard = (cards: HTMLElement[], container: HTMLDivElement, paddingLeft: number): number => {
	return cards.findIndex((card) => {
		const position = getCardPosition(card, container);
		return position >= paddingLeft - 10; // 10px tolerance
	});
};

const getTargetCardIndex = (currentIndex: number, totalCards: number, direction: 'left' | 'right'): number => {
	if (direction === 'right') return Math.min(currentIndex + 1, totalCards - 1);
	return Math.max(currentIndex - 1, 0);
};

const calculateScrollToCard = (targetCard: HTMLElement, container: HTMLDivElement, paddingLeft: number): number => {
	const targetPosition = getCardPosition(targetCard, container);
	return targetPosition - paddingLeft;
};

const calculateScrollToBoundary = (container: HTMLDivElement, direction: 'left' | 'right'): number => {
	const { scrollLeft, scrollWidth, clientWidth } = container;
	if (direction === 'right') return scrollWidth - clientWidth - scrollLeft;
	return -scrollLeft;
};

const calculateScrollDistance = (container: HTMLDivElement, direction: 'left' | 'right'): number => {
	const cards = getAllCards(container);
	if (cards.length === 0) return 0;

	const paddingLeft = getContainerPadding(container);
	const currentIndex = findVisibleCard(cards, container, paddingLeft);

	if (currentIndex === -1) {
		const cardWidth = cards[0]?.offsetWidth || 300;
		return direction === 'right' ? cardWidth : -cardWidth;
	}

	const targetIndex = getTargetCardIndex(currentIndex, cards.length, direction);

	if (targetIndex === currentIndex) {
		return calculateScrollToBoundary(container, direction);
	}

	return calculateScrollToCard(cards[targetIndex], container, paddingLeft);
};

const isCardAlignedToStart = (card: HTMLElement, container: HTMLDivElement): boolean => {
	const paddingLeft = getContainerPadding(container);
	const cardLeft = getCardPosition(card, container);
	return Math.abs(cardLeft - paddingLeft) <= 2;
};

const areCardsAlignedProperly = (cards: HTMLElement[], container: HTMLDivElement) => {
	if (cards.length === 0) return { firstAligned: false, lastAligned: false };

	const firstAligned = isCardAlignedToStart(cards[0], container);
	const lastAligned = isCardAlignedToStart(cards[cards.length - 1], container);

	return { firstAligned, lastAligned };
};

const useScrollStateUpdates = (containerRef: React.RefObject<HTMLDivElement | null>, updateState: () => void) => {
	// Watch for content changes (cards added/removed)
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;

		requestAnimationFrame(updateState);

		const observer = new MutationObserver(() => setTimeout(updateState, 50));
		observer.observe(container, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, [containerRef, updateState]);

	// Watch for container size changes
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;

		const observer = new ResizeObserver(() => setTimeout(updateState, 100));
		observer.observe(container);
		return () => observer.disconnect();
	}, [containerRef, updateState]);

	// Watch for scroll position changes
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;

		container.addEventListener('scroll', updateState, { passive: true });
		return () => container.removeEventListener('scroll', updateState);
	}, [containerRef, updateState]);

	// Watch for viewport changes
	useEffect(() => {
		const handleResize = () => setTimeout(updateState, 100);
		window.addEventListener('resize', handleResize, { passive: true });
		return () => window.removeEventListener('resize', handleResize);
	}, [updateState]);
};

type UseMediaHitScrollControlData = {
	canScrollLeft: boolean;
	canScrollRight: boolean;
	hasOverflow: boolean;
};

type UseMediaHitScrollControlActions = {
	updateScrollState: () => void;
	scrollByOneMediaHit: (direction: 'left' | 'right') => void;
};

type UseMediaHitScrollControlResult = ModelHookResult<UseMediaHitScrollControlData, UseMediaHitScrollControlActions>;

const scrollToPosition = (container: HTMLDivElement, direction: 'left' | 'right', onComplete: () => void) => {
	const distance = calculateScrollDistance(container, direction);
	const newPosition = container.scrollLeft + distance;
	const maxPosition = container.scrollWidth - container.clientWidth;
	const clampedPosition = Math.max(0, Math.min(newPosition, maxPosition));

	container.scrollTo({ left: clampedPosition, behavior: 'smooth' });
	setTimeout(onComplete, 300);
};
const calculateButtonStates = (container: HTMLDivElement) => {
	const cards = getAllCards(container);
	const hasOverflow = container.scrollWidth > container.clientWidth;

	if (!hasOverflow || cards.length === 0) {
		return { canScrollLeft: false, canScrollRight: false, hasOverflow };
	}

	const { scrollLeft, scrollWidth, clientWidth } = container;
	const maxScroll = scrollWidth - clientWidth;
	const isAtStart = scrollLeft <= 1;
	const isAtEnd = scrollLeft >= maxScroll - 1;

	const { firstAligned, lastAligned } = areCardsAlignedProperly(cards, container);

	return {
		canScrollLeft: !isAtStart && !firstAligned,
		canScrollRight: !isAtEnd && !lastAligned,
		hasOverflow,
	};
};

export function useMediaHitScrollControl(
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
): UseMediaHitScrollControlResult {
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [hasOverflow, setHasOverflow] = useState(false);

	const updateScrollState = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const { canScrollLeft: left, canScrollRight: right, hasOverflow: overflow } = calculateButtonStates(container);
		setCanScrollLeft(left);
		setCanScrollRight(right);
		setHasOverflow(overflow);
	}, [scrollContainerRef]);

	useScrollStateUpdates(scrollContainerRef, updateScrollState);

	const scrollByOneMediaHit = useCallback(
		(direction: 'left' | 'right') => {
			const container = scrollContainerRef.current;
			if (!container) return;

			scrollToPosition(container, direction, updateScrollState);
		},
		[scrollContainerRef, updateScrollState],
	);

	return {
		data: { canScrollLeft, canScrollRight, hasOverflow },
		actions: { updateScrollState, scrollByOneMediaHit },
	};
}
